// Castling geometry and rights. Standard chess only: the king starts on the
// e-file and the rooks in the corners. Chess960 would replace this module
// behind the RuleSet seam.
import { isSquareAttacked } from './attacks.js';
import { makeMove } from './moveGen.js';
import {
  CASTLE_BK, CASTLE_BQ, CASTLE_WK, CASTLE_WQ, EMPTY, FLAG_CASTLE_KING, FLAG_CASTLE_QUEEN, KING, ROOK,
  opposite, pieceCode, type Color, type Move, type Position, type Square,
} from './types.js';
import { parseSquare } from './squares.js';

interface CastleSide {
  readonly color: Color;
  readonly right: number;
  readonly flag: number;
  readonly kingFrom: Square;
  readonly kingTo: Square;
  readonly rookFrom: Square;
  readonly rookTo: Square;
  /** Squares that must be empty between king and rook. */
  readonly empty: readonly Square[];
  /** Squares the king stands on, crosses or lands on — none may be attacked. */
  readonly safe: readonly Square[];
}

function side(color: Color, right: number, flag: number, k: string, kTo: string, r: string, rTo: string, empty: string[], safe: string[]): CastleSide {
  return {
    color, right, flag,
    kingFrom: parseSquare(k), kingTo: parseSquare(kTo),
    rookFrom: parseSquare(r), rookTo: parseSquare(rTo),
    empty: empty.map(parseSquare), safe: safe.map(parseSquare),
  };
}

export const CASTLE_SIDES: readonly CastleSide[] = [
  side('w', CASTLE_WK, FLAG_CASTLE_KING, 'e1', 'g1', 'h1', 'f1', ['f1', 'g1'], ['e1', 'f1', 'g1']),
  side('w', CASTLE_WQ, FLAG_CASTLE_QUEEN, 'e1', 'c1', 'a1', 'd1', ['b1', 'c1', 'd1'], ['e1', 'd1', 'c1']),
  side('b', CASTLE_BK, FLAG_CASTLE_KING, 'e8', 'g8', 'h8', 'f8', ['f8', 'g8'], ['e8', 'f8', 'g8']),
  side('b', CASTLE_BQ, FLAG_CASTLE_QUEEN, 'e8', 'c8', 'a8', 'd8', ['b8', 'c8', 'd8'], ['e8', 'd8', 'c8']),
];

/** The castle side a move performs, if it is a castling move. */
export function castleSideOf(move: Move): CastleSide | undefined {
  return CASTLE_SIDES.find((s) => (move.flags & s.flag) !== 0 && s.kingFrom === move.from);
}

/** Drop rights whose king or rook is not on its home square (e.g. from a pasted FEN). */
export function sanitizeCastling(board: Int8Array, rights: number): number {
  let result = rights;
  for (const s of CASTLE_SIDES) {
    const kingHome = board[s.kingFrom] === pieceCode(KING, s.color);
    const rookHome = board[s.rookFrom] === pieceCode(ROOK, s.color);
    if (!kingHome || !rookHome) result &= ~s.right;
  }
  return result;
}

/** Rights that survive a move touching `from` and `to` (king/rook moves, rook captures). */
export function updateCastling(rights: number, from: Square, to: Square): number {
  let result = rights;
  for (const s of CASTLE_SIDES) {
    if (from === s.kingFrom || from === s.rookFrom || to === s.rookFrom) result &= ~s.right;
  }
  return result;
}

/** Fully legal castling moves for the side to move. */
export function generateCastling(pos: Position): Move[] {
  const out: Move[] = [];
  const enemy = opposite(pos.turn);
  for (const s of CASTLE_SIDES) {
    if (s.color !== pos.turn || (pos.castling & s.right) === 0) continue;
    if (s.empty.some((sq) => pos.board[sq] !== EMPTY)) continue;
    if (s.safe.some((sq) => isSquareAttacked(pos.board, sq, enemy))) continue;
    out.push(makeMove(s.kingFrom, s.kingTo, pieceCode(KING, s.color), EMPTY, s.flag));
  }
  return out;
}
